// What kind of property a site is, in an operator's words (ADR-0235, ADR-0422;
// #1325). The keys are the contract's `VerticalKey`; only the labels are the
// web application's, and the typecheck refuses a key without one.
//
// **A label and nothing more.** The words a vertical changes on other screens —
// guest or resident, room or unit — are the lexicon's (`lexicon.ts`, ADR-0477,
// #2032), and nothing here reads a vertical to say anything but its label.
import { VERTICAL_KEY } from "./api/enums.gen";
import type { components } from "./api/schema";

export type VerticalKey = components["schemas"]["VerticalKey"];
// The named list: since ADR-0511 the field is the list or any other string.
type Source = components["schemas"]["SiteEffectiveVerticalSource"];

/** ADR-0422 decision 1, in #1031's wording. */
export const VERTICAL_LABEL: Record<VerticalKey, string> = {
  hospitality: "Hospitality",
  senior: "Senior Living",
  mdu: "MDU",
  corporate: "Corporate",
  retail: "Retail",
};

// Restates ADR-0235 decision 5a — where an effective vertical came from: the
// site's own, its catalogue brand's chain's (ADR-0486 decision 7a),
// or neither, which is also how a stored key the list no longer carries reads.
export const VERTICAL_SOURCE: Record<Source, string> = {
  explicit: "Set here",
  chain: "From the chain",
  none: "None",
};

/** The list as a select offers it, in the contract's order. */
export const VERTICAL_OPTIONS = VERTICAL_KEY.map((k) => ({ value: k, label: VERTICAL_LABEL[k] }));

/** Whether a stored key is on the list. One that is not was written before the
 * list moved: it is shown as stored and offered nowhere. */
export const isVerticalKey = (k: string | undefined): k is VerticalKey => k !== undefined && (VERTICAL_KEY as readonly string[]).includes(k);

/** A select's options for a record that holds `stored`: the list, and the
 * stored key too when the list no longer carries it, so opening the form does
 * not read it as blank. */
export function verticalOptions(stored: string | undefined): { value: string; label: string }[] {
  return stored && !isVerticalKey(stored) ? [...VERTICAL_OPTIONS, { value: stored, label: `${stored} (no longer offered)` }] : VERTICAL_OPTIONS;
}

/** What an edit form sends for a vertical: nothing when the choice is what the
 * record held — so a key the list no longer carries is not sent back and
 * refused — `null` when a held value was emptied, else the chosen key. */
export function verticalPatch(chosen: string | undefined, stored: string | undefined): VerticalKey | null | undefined {
  const now = chosen ?? "";
  if (now === (stored ?? "")) return undefined;
  return now === "" ? null : (now as VerticalKey);
}
